import React, { useCallback, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import { DataGrid, GridAlignment, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';

import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';

import { Loader, ButtonRow, Message } from '@omnigenbiodata/ui';

import InnerLayout from '../../../../../../layout/Inner';
import { useAppDispatch, useAppSelector } from '../../../../../../store';
import { ROUTES } from '../../../../../../core/constants/routes.constants';

import {
  listShipmentsHasErrorSelector,
  listShipmentsIsBusySelector,
  listShipmentsResponseSelector,
  listShipmentsThunk,
} from '../../../../../../store/shipmentList';

import { getShipmentManifestThunk } from '../../../../../../store/shipmentGetManifest/thunks';
import {
  getShipmentManifestResponseSelector,
  manifestDownloadReset,
} from '../../../../../../store/shipmentGetManifest';

import { manifestStagedShipmentIsBusySelector } from '../../../../../../store/stagedShipmentManifest/selectors';

interface ShipmentRow {
  shipmentId: string;
  totalAliquotCount: number;
  destination: string;
  shipmentType: string;
  status: string;
  createdTs: string;
}

function getStatusTone(status: string) {
  switch (status) {
    case 'MANIFESTED':
      return {
        color: 'warning' as const,
        tooltip: 'Shipment has been manifested.',
      };

    case 'SHIPPED':
      return {
        color: 'success' as const,
        tooltip: 'Shipment has been completed.',
      };

    default:
      return {
        color: 'primary' as const,
        tooltip: 'Shipment is in progress.',
      };
  }
}

function formatDateTime(value: string | null | undefined) {
  if (!value) {
    return 'Not recorded';
  }

  return moment(value).format('DD-MM-YY h:mma');
}

function buildColumns(downloadManifest: (shipmentId: string) => void): GridColDef<ShipmentRow>[] {
  return [
    {
      field: 'shipmentId',
      headerName: 'Shipment ID',
      flex: 1.1,
      renderCell: (params: GridRenderCellParams<ShipmentRow, string>) => (
        <Box
          sx={{
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25,
            overflow: 'hidden',
          }}
        >
          <Typography
            variant="body2"
            color="primary"
            sx={{
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {params.value}
          </Typography>

          <Typography variant="caption" component="span" color="text.secondary">
            Shipment
          </Typography>
        </Box>
      ),
    },
    {
      field: 'totalAliquotCount',
      headerName: 'Samples',
      flex: 0.55,
      align: 'center' as GridAlignment,
      headerAlign: 'center' as GridAlignment,
      renderCell: (params: GridRenderCellParams<ShipmentRow, number>) => (
        <Chip
          label={params.value ?? 0}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: 700,
            minWidth: 54,
          }}
        />
      ),
    },
    {
      field: 'destination',
      headerName: 'Destination',
      flex: 0.9,
      renderCell: (params: GridRenderCellParams<ShipmentRow, string>) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {params.value || 'Not recorded'}
        </Typography>
      ),
    },
    {
      field: 'shipmentType',
      headerName: 'Type',
      flex: 0.75,
      renderCell: (params: GridRenderCellParams<ShipmentRow, string>) => (
        <Chip
          label={params.value || 'UNKNOWN'}
          size="small"
          variant="outlined"
          icon={<Inventory2Outlined />}
          sx={{
            fontWeight: 700,
            maxWidth: '100%',
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
            '& .MuiChip-icon': {
              fontSize: 18,
            },
          }}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.85,
      align: 'center' as GridAlignment,
      headerAlign: 'center' as GridAlignment,
      renderCell: (params: GridRenderCellParams<ShipmentRow, string>) => {
        const status = params.value || 'UNKNOWN';
        const tone = getStatusTone(status);

        return (
          <Tooltip title={tone.tooltip}>
            <Chip
              icon={<LocalShippingOutlined />}
              label={status}
              size="small"
              color={tone.color}
              sx={{
                fontWeight: 700,
                maxWidth: '100%',
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
                '& .MuiChip-icon': {
                  fontSize: 18,
                },
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'createdTs',
      headerName: 'Created',
      flex: 0.8,
      renderCell: (params: GridRenderCellParams<ShipmentRow, string>) => (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {formatDateTime(params.value)}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 96,
      align: 'right' as GridAlignment,
      headerAlign: 'right' as GridAlignment,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<ShipmentRow>) => (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 0.5,
          }}
        >
          <Tooltip title="Download manifest">
            <IconButton
              data-testid={`manifest-download-${params.id}`}
              onClick={(event) => {
                event.stopPropagation();
                downloadManifest(String(params.id));
              }}
              aria-label="Download Manifest"
              title="Download Manifest"
              size="small"
              sx={{
                border: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                '&:hover': {
                  borderColor: 'primary.light',
                  bgcolor: 'primary.lighter',
                },
              }}
            >
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="View shipment">
            <IconButton
              data-testid={`manifest-view-${params.id}`}
              aria-label="View Manifest"
              title="View Manifest"
              size="small"
              component={Link}
              to={ROUTES.shippingView.replace(':shippingId', String(params.id))}
              sx={{
                border: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                '&:hover': {
                  borderColor: 'primary.light',
                  bgcolor: 'primary.lighter',
                },
              }}
            >
              <VisibilityOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];
}

function ShippingHistoryScene() {
  const { t } = useTranslation('portal');
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));

  const errors = useAppSelector(listShipmentsHasErrorSelector);
  const shipmentsList = useAppSelector(listShipmentsResponseSelector);
  const isBusy = useAppSelector(listShipmentsIsBusySelector);
  const manifest = useAppSelector(getShipmentManifestResponseSelector);
  const manifestLoading = useAppSelector(manifestStagedShipmentIsBusySelector);

  const navigate = useNavigate();
  const goHome = () => navigate(ROUTES.root);

  useEffect(() => {
    dispatch(listShipmentsThunk());
  }, [dispatch]);

  useEffect(() => {
    if (manifest?.link) {
      window.open(manifest.link, '_blank');
    }
  }, [dispatch, manifest]);

  const downloadManifest = useCallback(
    (shipmentId: string) => {
      if (shipmentId) {
        dispatch(getShipmentManifestThunk(shipmentId));
      }
    },
    [dispatch],
  );

  useEffect(() => {
    return () => {
      dispatch(manifestDownloadReset());
    };
  }, [dispatch]);

  const gridColumns = useMemo(() => buildColumns(downloadManifest), [downloadManifest]);

  return (
    <InnerLayout>
      <Typography mb={2} variant="h3" align="center">
        {t('shippingHistory.title')}
      </Typography>

      <Container maxWidth="sm" sx={{ paddingX: 0, paddingY: 2 }}>
        {errors && (
          <Message severity="error" title={t('alerts.shippingHistoryFailed.title') as string}>
            - Please try again later.
          </Message>
        )}
      </Container>

      <Box mb={4} sx={{ bgcolor: 'background.paper' }}>
        <Paper
          elevation={4}
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            mt: 2,
          }}
        >
          <Box
            sx={{
              px: { xs: 2.5, md: 3 },
              pt: { xs: 2.5, md: 3 },
              pb: 2,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
              Shipments
            </Typography>

            <Typography variant="body2" component="p" color="text.secondary" sx={{ mt: 0.5 }}>
              {shipmentsList && shipmentsList.length}{' '}
              {shipmentsList && shipmentsList.length > 1 ? 'shipments' : 'shipment'} found
            </Typography>
          </Box>

          <DataGrid
            rows={(shipmentsList || []) as ShipmentRow[]}
            autoHeight
            columnBuffer={2}
            getRowId={(row) => row.shipmentId}
            columns={gridColumns}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 50 },
              },
            }}
            columnVisibilityModel={{
              destination: !isSmallScreen,
              shipmentType: !isMediumScreen,
              createdTs: !isSmallScreen,
            }}
            getRowClassName={(params: GridRowParams<ShipmentRow>) =>
              `shipment-row shipment-row--${String(params.row.status || 'unknown').toLowerCase()}`
            }
            disableRowSelectionOnClick
            disableColumnMenu
            sx={{
              border: 0,
              width: '100%',

              '& .MuiDataGrid-main': {
                overflow: 'hidden',
              },

              '& .MuiDataGrid-virtualScroller': {
                overflowX: 'hidden',
              },

              '& .MuiDataGrid-columnHeaders': {
                bgcolor: 'grey.50',
                borderBottom: 1,
                borderBottomColor: 'divider',
              },

              '& .MuiDataGrid-columnHeader': {
                bgcolor: 'grey.50',
                px: 1,
              },

              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
                color: 'text.secondary',
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },

              '& .MuiDataGrid-cell': {
                borderBottomColor: 'divider',
                px: 1,
                overflow: 'hidden',
              },

              '& .MuiDataGrid-cellContent': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },

              '& .MuiDataGrid-row': {
                borderLeft: 4,
                borderLeftColor: 'primary.main',
              },

              '& .MuiDataGrid-row:hover': {
                bgcolor: 'grey.50',
              },

              '& .shipment-row--manifested': {
                bgcolor: 'warning.lighter',
                borderLeftColor: 'warning.main',
              },

              '& .shipment-row--manifested:hover': {
                bgcolor: 'warning.lighter',
              },

              '& .shipment-row--shipped, & .shipment-row--finalised, & .shipment-row--finalized': {
                bgcolor: 'background.paper',
                borderLeftColor: 'success.main',
              },

              '& .shipment-row--shipped:hover, & .shipment-row--finalised:hover, & .shipment-row--finalized:hover': {
                bgcolor: 'grey.50',
              },

              '& .shipment-row--cancelled, & .shipment-row--failed': {
                bgcolor: 'error.lighter',
                borderLeftColor: 'error.main',
              },

              '& .shipment-row--cancelled:hover, & .shipment-row--failed:hover': {
                bgcolor: 'error.lighter',
              },

              '& .MuiDataGrid-footerContainer': {
                borderTop: 1,
                borderTopColor: 'divider',
                bgcolor: 'grey.50',
              },
            }}
          />
        </Paper>
      </Box>

      <ButtonRow
        buttonSize="large"
        backLabel={t('buttons.home') as string}
        showForward={false}
        onBack={goHome}
        forwardColor="primary"
        backIcon={null}
      />

      <Loader isVisible={manifestLoading} label={t('loaders.manifest') as string} />
      <Loader isVisible={isBusy} label={t('loaders.loading') as string} />
    </InnerLayout>
  );
}

export default ShippingHistoryScene;
